import { useState, useMemo } from 'react';

/**
 * Hook for sortable table data.
 * Click once: ascending. Click again: descending. Click again: back to original order.
 *
 * Usage:
 *   const { sortedData, sortConfig, requestSort, getSortIndicator } = useSortableData(data);
 *   <th onClick={() => requestSort('amount')} style={{ cursor: 'pointer' }}>
 *     Amount {getSortIndicator('amount')}
 *   </th>
 */
export default function useSortableData(data) {
  // sortConfig: { key, direction } or null
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = (key) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null; // back to original
    });
  };

  const sortedData = useMemo(() => {
    if (!data || !sortConfig) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle nulls/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      // Try to parse DD/MM/YYYY dates
      const dateA = parseDateDMY(aStr);
      const dateB = parseDateDMY(bStr);
      if (dateA && dateB) {
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' \u2195'; // ↕
    return sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193'; // ↑ or ↓
  };

  return { sortedData, sortConfig, requestSort, getSortIndicator };
}

function parseDateDMY(str) {
  const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1])).getTime();
}
